/**
 * Roles and what they are allowed to do. Capabilities are declared here once; every consumer
 * (server authorization, client affordances) derives behaviour from these tables instead of
 * comparing role literals.
 */

export const ACCOUNT_ROLES = ['owner', 'host'] as const;
export type AccountRole = (typeof ACCOUNT_ROLES)[number];

export interface AccountRoleCapabilities {
  /** Invite new hosts, disable and re-enable accounts. */
  readonly manageAccounts: boolean;
}

export const ACCOUNT_ROLE_CAPABILITIES = {
  owner: { manageAccounts: true },
  host: { manageAccounts: false },
} as const satisfies Record<AccountRole, AccountRoleCapabilities>;

/** Role of someone inside a screening room. Every active account is a host in every room. */
export const PARTICIPANT_ROLES = ['host', 'guest'] as const;
export type ParticipantRole = (typeof PARTICIPANT_ROLES)[number];

export interface ParticipantRoleCapabilities {
  /** Play, pause and seek regardless of the screening control policy. */
  readonly controlPlayback: boolean;
  /** Mute other participants and remove them from the room. */
  readonly moderate: boolean;
  /** Edit the screening (title, film, time, control policy), rotate the invite, end it. */
  readonly manageScreening: boolean;
}

export const PARTICIPANT_ROLE_CAPABILITIES = {
  host: { controlPlayback: true, moderate: true, manageScreening: true },
  guest: { controlPlayback: false, moderate: false, manageScreening: false },
} as const satisfies Record<ParticipantRole, ParticipantRoleCapabilities>;

/** Facts about a participant role as the target of moderation. */
export interface ParticipantRoleTraits {
  /** May be refused from rejoining a screening ("remove and ban"). Hosts never are. */
  readonly bannable: boolean;
}

export const PARTICIPANT_ROLE_TRAITS = {
  host: { bannable: false },
  guest: { bannable: true },
} as const satisfies Record<ParticipantRole, ParticipantRoleTraits>;
